import { useEffect, useState, type MouseEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiGetParticipantTicketLink, toTicketPathWithFrom } from '../utils/participantMappers'
import { ButtonLink } from './Button'

const TicketIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
  </svg>
)

/**
 * Organiser "Ticket" action used across Event Management participant tables.
 *
 * The `tk` token is derived server-side, so the real magic-link href cannot be built from
 * the event/participant ids. This button fetches `/ticket-link` on mount and writes that URL
 * onto the `<a href>` — so right-click → Open in new tab, middle-click, and copy-link all
 * land on the participant ticket instead of the token-less `/e/:id/ticket` fallback (which
 * 404s on a fresh tab because TicketPage's no-`tk` path needs a published event in Redux).
 *
 * Left-click still preventDefault + SPA-navigates (and Ctrl/Cmd/Shift+click still window.open)
 * so the existing in-app behaviour and tests stay intact, including the `from` return path.
 */
export function ParticipantTicketButton({
  eventId,
  participantId,
  fullWidth = false,
  className = '',
}: {
  eventId: string
  participantId?: string
  fullWidth?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const from = encodeURIComponent(`${location.pathname}${location.search}`)
  const fallbackTo = `/e/${eventId}/ticket`
  const [ticketUrl, setTicketUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!participantId) return
    let cancelled = false
    void apiGetParticipantTicketLink(participantId).then((url) => {
      if (cancelled || !url) return
      setTicketUrl(url)
    })
    return () => {
      cancelled = true
    }
  }, [participantId])

  const to = ticketUrl ? toTicketPathWithFrom(ticketUrl, from) : fallbackTo

  async function handleOpenTicket(e: MouseEvent) {
    if (!participantId) return
    const openInNewTab = e.metaKey || e.ctrlKey || e.shiftKey
    e.preventDefault()
    const url = ticketUrl ?? (await apiGetParticipantTicketLink(participantId))
    if (!url) return
    if (openInNewTab) {
      window.open(`${url}&from=${from}`, '_blank', 'noopener,noreferrer')
      return
    }
    try {
      navigate(toTicketPathWithFrom(url, from))
    } catch {
      window.location.href = url
    }
  }

  return (
    <ButtonLink
      to={to}
      variant="secondary"
      size="sm"
      fullWidth={fullWidth}
      className={className}
      leftIcon={<TicketIcon />}
      onClick={handleOpenTicket}
    >
      {t('acc_ticket_link')}
    </ButtonLink>
  )
}
